// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — the whole simulation. No DOM, no canvas, no timers of its own.
 *
 * THE RULE THAT MAKES THIS NOT A STACKER CLONE: the well hangs from a pivot.
 * Every cell has mass; the stack's torque about the centre column gives the
 * well a signed LIST in [-1, 1]. A listing well curves the fall (a piece slides
 * one column downhill every `driftRows` rows, capped at `maxDrift`), which makes
 * the far side of a listing well genuinely unreachable — and clearing while
 * IN TRIM pays double. Those two pull against each other and that is the game.
 *
 * Determinism: everything shared is a pure function of (seed, mode, inputs).
 * The bag stream and the ballast-gap stream are SEPARATE rngs on purpose, so an
 * incoming attack can never shift the piece sequence — the sequence stays a
 * pure function of the seed no matter what the network does.
 */

import { makeRng, randInt, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import {
  BAG_TYPES, EMPTY, KICKS, T_BALLAST, T_CARGO, T_INGOT,
  cellsAt, massOf, pieceOf, type Cell,
} from './pieces';
import { ballastGap, dropInterval, type Mode } from './modes';

/** Score per simultaneous rows cleared, before level and trim multipliers. */
export const LINE_SCORES = [0, 100, 300, 500, 800];
/** Ballast sent per simultaneous rows cleared, before the trim bonus. */
export const ATTACK = [0, 0, 1, 2, 4];
/** |list| inside this band counts as IN TRIM. */
export const TRIM_BAND = 0.15;
/** How long a grounded piece may sit before it locks. */
export const LOCK_MS = 500;
/** How many times a move/rotate may reset that timer, so you cannot stall forever. */
export const MAX_LOCK_RESETS = 15;
/** Next-queue depth kept topped up. */
const QUEUE_MIN = 7;

export type GameEvent =
  | { t: 'spawn'; type: number; x: number; y: number }
  | { t: 'move'; dx: number }
  | { t: 'rotate'; dir: number }
  /**
   * `cells` is the exact footprint the piece moved INTO (or, when blocked, the
   * one it tried to move into). It rides along so an auditor can check the move
   * against the board directly, without having to reconstruct the piece's
   * orientation — recovering rotation from a normalised footprint is ambiguous
   * (an I-piece at rot 0 and rot 2 normalise identically), and an audit that
   * guessed wrong reported false violations.
   */
  | { t: 'drift'; dx: number; x: number; y: number; cells: Cell[]; blocked: boolean; list: number }
  | { t: 'lock'; type: number; cells: Cell[]; list: number; inTrim: boolean }
  | { t: 'clear'; rows: number[]; n: number; list: number; inTrim: boolean; score: number }
  | { t: 'attack'; n: number }
  | { t: 'ballast'; n: number; gapStart: number; gap: number; list: number }
  | { t: 'hold' }
  | { t: 'harddrop'; rows: number }
  | { t: 'levelup'; level: number }
  | { t: 'over'; reason: 'blocked' | 'overflow' };

export interface Active {
  type: number;
  rot: number;
  x: number;
  y: number;
  /** Rows descended since spawn — drift is bought with these, not with time. */
  rowsFallen: number;
  /** Columns already drifted this piece. */
  drifts: number;
}

export interface GameConfig {
  mode: Mode;
  seed: number;
  /**
   * Which way a falling piece slides when the well lists.
   * +1 = downhill, toward the heavy side (the physical answer, and the one the
   * balance sim had to referee because "the heavy side gets heavier" is exactly
   * the shape of a snowball). -1 = uphill. Sweepable; see balance.test.ts.
   */
  driftDir?: 1 | -1;
  /** Append every event to `journal` for the mechanism audit. Off in the browser. */
  journal?: boolean;
}

export interface GameStats {
  score: number;
  lines: number;
  level: number;
  pieces: number;
  /** Placements made while the well was in trim — the "did you sail it well" number. */
  trimmed: number;
  maxCombo: number;
  quads: number;
  ballastSent: number;
  ballastReceived: number;
}

export class Game {
  readonly mode: Mode;
  readonly cols: number;
  readonly rows: number;
  readonly seed: number;
  readonly cells: Uint8Array;
  private readonly bagRng: Rng;
  private readonly junkRng: Rng;
  private readonly driftDir: 1 | -1;
  private readonly keepJournal: boolean;

  cur: Active | null = null;
  queue: number[] = [];
  hold: number | null = null;
  holdUsed = false;
  over = false;
  overReason: 'blocked' | 'overflow' | null = null;

  score = 0;
  lines = 0;
  level = 1;
  combo = 0;
  pieces = 0;
  trimmed = 0;
  maxCombo = 0;
  quads = 0;
  ballastSent = 0;
  ballastReceived = 0;
  pendingBallast = 0;
  elapsedMs = 0;

  /** Drained by the renderer for juice. */
  events: GameEvent[] = [];
  /** Append-only, only when `journal` is on. The mechanism audit reads THIS. */
  journal: GameEvent[] = [];

  private bagIndex = 0;
  private gravAcc = 0;
  private lockTimer = 0;
  private lockResets = 0;
  private listCache: number | null = null;

  constructor(config: GameConfig) {
    this.mode = config.mode;
    this.cols = config.mode.cols;
    this.rows = config.mode.rows;
    this.seed = config.seed;
    this.driftDir = config.driftDir ?? 1;
    this.keepJournal = config.journal ?? false;
    this.cells = new Uint8Array(this.cols * this.rows);
    this.bagRng = makeRng(config.seed >>> 0);
    this.junkRng = makeRng((config.seed ^ 0x9e3779b9) >>> 0);
    this.layCargo();
    this.fillQueue();
    this.spawn();
  }

  // ---------------------------------------------------------------- board ---

  idx(c: number, r: number): number {
    return r * this.cols + c;
  }

  at(c: number, r: number): number {
    if (c < 0 || c >= this.cols || r < 0 || r >= this.rows) return -1;
    return this.cells[this.idx(c, r)];
  }

  private emit(e: GameEvent): void {
    this.events.push(e);
    if (this.keepJournal) this.journal.push(e);
  }

  /** Renderer-side. The sim and the audit never call this. */
  drainEvents(): GameEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /**
   * How the well hangs, in [-1, 1]. Negative is heavy to port (left).
   *
   * torque / (maxTorque + keel). The keel is a built-in centred counterweight:
   * it damps a nearly-empty well (one piece in a corner should not read as a
   * full capsize) and matters less as the stack grows, so the well gets more
   * responsive to imbalance exactly as imbalance starts to matter.
   */
  list(): number {
    if (this.listCache !== null) return this.listCache;
    const centre = (this.cols - 1) / 2;
    let torque = 0;
    let maxT = 0;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const v = this.cells[this.idx(c, r)];
        if (v === EMPTY) continue;
        const m = massOf(v);
        torque += m * (c - centre);
        maxT += m * Math.abs(c - centre);
      }
    }
    const l = torque / (maxT + this.mode.keel);
    this.listCache = Math.max(-1, Math.min(1, l));
    return this.listCache;
  }

  inTrim(): boolean {
    return Math.abs(this.list()) < TRIM_BAND;
  }

  private dirty(): void {
    this.listCache = null;
  }

  /** Height of the tallest column, 0..rows. Drives the danger rim. */
  stackHeight(): number {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.cells[this.idx(c, r)] !== EMPTY) return this.rows - r;
      }
    }
    return 0;
  }

  /** Per-column heights — the opponent mini-wells send exactly this. */
  columnHeights(): number[] {
    const out: number[] = [];
    for (let c = 0; c < this.cols; c++) {
      let h = 0;
      for (let r = 0; r < this.rows; r++) {
        if (this.cells[this.idx(c, r)] !== EMPTY) { h = this.rows - r; break; }
      }
      out.push(h);
    }
    return out;
  }

  // ---------------------------------------------------------------- setup ---

  private layCargo(): void {
    const n = this.mode.cargo;
    if (n <= 0) return;
    const band = Math.min(4, this.rows);
    const spots: number[] = [];
    for (let r = this.rows - band; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) spots.push(this.idx(c, r));
    }
    const picked = shuffle(this.bagRng, spots).slice(0, n);
    // Never hand the player a row that is already full or one cell from full.
    const perRow = new Map<number, number>();
    for (const i of picked) {
      const r = Math.floor(i / this.cols);
      const count = (perRow.get(r) ?? 0) + 1;
      if (count > this.cols - 3) continue;
      perRow.set(r, count);
      this.cells[i] = T_CARGO;
    }
    this.dirty();
  }

  private fillQueue(): void {
    while (this.queue.length < QUEUE_MIN) {
      const bag = shuffle(this.bagRng, BAG_TYPES);
      this.bagIndex++;
      if (this.mode.ingotEvery > 0 && this.bagIndex % this.mode.ingotEvery === 0) {
        bag.splice(randInt(this.bagRng, 0, bag.length), 0, T_INGOT);
      }
      this.queue.push(...bag);
    }
  }

  peek(n: number): number[] {
    return this.queue.slice(0, n);
  }

  // ------------------------------------------------------------ placement ---

  canPlace(type: number, rot: number, x: number, y: number): boolean {
    for (const [c, r] of cellsAt(type, rot, x, y)) {
      if (c < 0 || c >= this.cols || r < 0 || r >= this.rows) return false;
      if (this.cells[this.idx(c, r)] !== EMPTY) return false;
    }
    return true;
  }

  private spawnPos(type: number, rot = 0): { x: number; y: number } {
    const p = pieceOf(type);
    const cells = p.rotations[rot];
    let minY = Infinity;
    for (const [, cy] of cells) minY = Math.min(minY, cy);
    return { x: Math.floor((this.cols - p.n) / 2), y: -minY };
  }

  private spawn(type?: number): void {
    if (this.over) return;
    if (this.pendingBallast > 0) {
      const n = this.pendingBallast;
      this.pendingBallast = 0;
      this.applyBallast(n);
      if (this.over) return;
    }
    this.fillQueue();
    const t = type ?? this.queue.shift()!;
    const { x, y } = this.spawnPos(t);
    if (!this.canPlace(t, 0, x, y)) {
      this.over = true;
      this.overReason = 'blocked';
      this.cur = null;
      this.emit({ t: 'over', reason: 'blocked' });
      return;
    }
    this.cur = { type: t, rot: 0, x, y, rowsFallen: 0, drifts: 0 };
    this.gravAcc = 0;
    this.lockTimer = 0;
    this.lockResets = 0;
    this.holdUsed = false;
    this.emit({ t: 'spawn', type: t, x, y });
  }

  // -------------------------------------------------------------- falling ---

  /**
   * One row of descent, drift included. `commit` false runs it on a copy for the
   * ghost, so the ghost is EXACT — it shows where the piece really lands, drift
   * and all, which is what stops the curve reading as a betrayal.
   *
   * The list is read once per call and is constant while a piece falls (the
   * board only changes when something locks), so ghost and reality cannot
   * disagree.
   */
  private fallOnce(p: Active, list: number, commit: boolean): boolean {
    if (!this.canPlace(p.type, p.rot, p.x, p.y + 1)) return false;
    p.y += 1;
    p.rowsFallen += 1;
    const m = this.mode;
    if (p.drifts < m.maxDrift
      && p.rowsFallen >= (p.drifts + 1) * m.driftRows
      && Math.abs(list) >= m.listThreshold) {
      const dx = (list > 0 ? 1 : -1) * this.driftDir;
      const target = cellsAt(p.type, p.rot, p.x + dx, p.y);
      const ok = this.canPlace(p.type, p.rot, p.x + dx, p.y);
      if (ok) p.x += dx;
      // A blocked drift is still spent, so a piece pinned against a wall does
      // not re-test every single row for the rest of its fall.
      p.drifts += 1;
      if (commit) this.emit({ t: 'drift', dx, x: p.x, y: p.y, cells: target, blocked: !ok, list });
    }
    return true;
  }

  /** Where the current piece will actually come to rest. */
  ghost(): Active | null {
    if (!this.cur) return null;
    const p: Active = { ...this.cur };
    const list = this.list();
    let guard = this.rows + 4;
    while (guard-- > 0 && this.fallOnce(p, list, false)) { /* fall */ }
    return p;
  }

  // --------------------------------------------------------------- inputs ---

  move(dx: number): boolean {
    if (!this.cur || this.over) return false;
    if (!this.canPlace(this.cur.type, this.cur.rot, this.cur.x + dx, this.cur.y)) return false;
    this.cur.x += dx;
    this.touchLock();
    this.emit({ t: 'move', dx });
    return true;
  }

  rotate(dir: 1 | -1): boolean {
    if (!this.cur || this.over) return false;
    const rot = (((this.cur.rot + dir) % 4) + 4) % 4;
    for (const [kx, ky] of KICKS) {
      if (this.canPlace(this.cur.type, rot, this.cur.x + kx, this.cur.y + ky)) {
        this.cur.rot = rot;
        this.cur.x += kx;
        this.cur.y += ky;
        this.touchLock();
        this.emit({ t: 'rotate', dir });
        return true;
      }
    }
    return false;
  }

  /** One row down by the player's hand. Scores 1. */
  softDrop(): boolean {
    if (!this.cur || this.over) return false;
    const moved = this.fallOnce(this.cur, this.list(), true);
    if (moved) { this.score += 1; this.gravAcc = 0; }
    return moved;
  }

  hardDrop(): void {
    if (!this.cur || this.over) return;
    const list = this.list();
    let n = 0;
    let guard = this.rows + 4;
    while (guard-- > 0 && this.fallOnce(this.cur, list, true)) n++;
    this.score += n * 2;
    this.emit({ t: 'harddrop', rows: n });
    this.lock();
  }

  holdPiece(): boolean {
    if (!this.cur || this.over || this.holdUsed) return false;
    const held = this.hold;
    this.hold = this.cur.type;
    this.emit({ t: 'hold' });
    if (held === null) {
      this.spawn();
    } else {
      const { x, y } = this.spawnPos(held);
      if (!this.canPlace(held, 0, x, y)) {
        this.over = true;
        this.overReason = 'blocked';
        this.cur = null;
        this.emit({ t: 'over', reason: 'blocked' });
        return true;
      }
      this.cur = { type: held, rot: 0, x, y, rowsFallen: 0, drifts: 0 };
      this.gravAcc = 0;
      this.lockTimer = 0;
      this.lockResets = 0;
    }
    this.holdUsed = true;
    return true;
  }

  private touchLock(): void {
    if (this.lockTimer > 0 && this.lockResets < MAX_LOCK_RESETS) {
      this.lockTimer = 0;
      this.lockResets++;
    }
  }

  // ------------------------------------------------------------------ tick ---

  /**
   * Advance by real time. Gravity and the lock delay both live here, driven by
   * a delta the caller supplies — so the whole sim is testable without a clock
   * and a backgrounded tab cannot change the outcome.
   */
  step(dtMs: number): void {
    if (this.over) return;
    this.elapsedMs += dtMs;
    if (!this.cur) { this.spawn(); if (this.over || !this.cur) return; }

    const interval = dropInterval(this.mode, this.level);
    this.gravAcc += dtMs;
    let guard = 64;
    while (this.gravAcc >= interval && guard-- > 0) {
      this.gravAcc -= interval;
      if (!this.fallOnce(this.cur, this.list(), true)) break;
    }

    const grounded = !this.canPlace(this.cur.type, this.cur.rot, this.cur.x, this.cur.y + 1);
    if (grounded) {
      this.lockTimer += dtMs;
      if (this.lockTimer >= LOCK_MS || this.lockResets >= MAX_LOCK_RESETS) this.lock();
    } else {
      this.lockTimer = 0;
    }
  }

  // ------------------------------------------------------------------ lock ---

  private lock(): void {
    const p = this.cur;
    if (!p) return;
    const cells = cellsAt(p.type, p.rot, p.x, p.y);
    for (const [c, r] of cells) this.cells[this.idx(c, r)] = p.type;
    this.dirty();
    this.cur = null;
    this.pieces++;

    const list = this.list();
    const inTrim = Math.abs(list) < TRIM_BAND;
    if (inTrim) this.trimmed++;
    this.emit({ t: 'lock', type: p.type, cells, list, inTrim });

    const full: number[] = [];
    for (let r = 0; r < this.rows; r++) {
      let solid = true;
      for (let c = 0; c < this.cols; c++) {
        if (this.cells[this.idx(c, r)] === EMPTY) { solid = false; break; }
      }
      if (solid) full.push(r);
    }

    if (full.length > 0) {
      this.clearRows(full);
      const n = full.length;
      const gained = LINE_SCORES[n] * this.level * (inTrim ? 2 : 1)
        + (this.combo > 0 ? 50 * this.combo * this.level : 0);
      this.score += gained;
      this.lines += n;
      this.combo += 1;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      if (n >= 4) this.quads++;
      this.emit({ t: 'clear', rows: full, n, list, inTrim, score: gained });

      const sent = ATTACK[n] + (inTrim ? 1 : 0);
      if (sent > 0) { this.ballastSent += sent; this.emit({ t: 'attack', n: sent }); }

      const nextLevel = 1 + Math.floor(this.lines / 10);
      if (nextLevel !== this.level) { this.level = nextLevel; this.emit({ t: 'levelup', level: nextLevel }); }
    } else {
      this.combo = 0;
    }

    this.spawn();
  }

  private clearRows(rowsToClear: number[]): void {
    const kill = new Set(rowsToClear);
    const next = new Uint8Array(this.cols * this.rows);
    let write = this.rows - 1;
    for (let r = this.rows - 1; r >= 0; r--) {
      if (kill.has(r)) continue;
      for (let c = 0; c < this.cols; c++) next[write * this.cols + c] = this.cells[this.idx(c, r)];
      write--;
    }
    this.cells.set(next);
    this.dirty();
  }

  // --------------------------------------------------------------- ballast ---

  receiveBallast(n: number): void {
    if (n <= 0 || this.over) return;
    this.pendingBallast += n;
    this.ballastReceived += n;
  }

  /**
   * Rows rise from the floor, FILLED ON THE HEAVY SIDE with the gap on the light
   * side. So an attack both raises your stack and worsens your list, and the
   * only way to clear it is to fill the light side — which trims you.
   */
  private applyBallast(n: number): void {
    const gap = ballastGap(this.mode);
    const l = this.list();
    let gapStart: number;
    if (Math.abs(l) < 0.03) {
      gapStart = randInt(this.junkRng, 0, this.cols - gap);
    } else {
      // heavy to starboard (l > 0) => light side is port => gap at the left
      gapStart = l > 0 ? 0 : this.cols - gap;
    }
    for (let k = 0; k < n; k++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.cells[this.idx(c, 0)] !== EMPTY) {
          this.over = true;
          this.overReason = 'overflow';
          this.cur = null;
          this.emit({ t: 'over', reason: 'overflow' });
          return;
        }
      }
      for (let r = 0; r < this.rows - 1; r++) {
        for (let c = 0; c < this.cols; c++) {
          this.cells[this.idx(c, r)] = this.cells[this.idx(c, r + 1)];
        }
      }
      const last = this.rows - 1;
      for (let c = 0; c < this.cols; c++) {
        const inGap = c >= gapStart && c < gapStart + gap;
        this.cells[this.idx(c, last)] = inGap ? EMPTY : T_BALLAST;
      }
      this.dirty();
    }
    this.emit({ t: 'ballast', n, gapStart, gap, list: l });
  }

  // ------------------------------------------------------------- sim hooks ---

  /**
   * Position the piece at the top and drop it — exactly what a player does with
   * a hard drop, which is what makes this a fair model for the bot. Returns
   * false if that column/rotation is not reachable at the spawn row.
   */
  hardDropFrom(x: number, rot: number): boolean {
    if (!this.cur || this.over) return false;
    const spawnY = this.spawnPos(this.cur.type, 0).y;
    if (!this.canPlace(this.cur.type, rot, x, spawnY)) return false;
    this.cur.rot = rot;
    this.cur.x = x;
    this.cur.y = spawnY;
    this.cur.rowsFallen = 0;
    this.cur.drifts = 0;
    this.hardDrop();
    return true;
  }

  /** Every legal (column, rotation) and where it would actually land. */
  candidates(): { x: number; rot: number; land: Active }[] {
    if (!this.cur || this.over) return [];
    const out: { x: number; rot: number; land: Active }[] = [];
    const seen = new Set<string>();
    const type = this.cur.type;
    const spawnY = this.spawnPos(type, 0).y;
    const list = this.list();
    for (let rot = 0; rot < 4; rot++) {
      const sig = JSON.stringify(pieceOf(type).rotations[rot]);
      if (seen.has(sig)) continue;
      seen.add(sig);
      for (let x = -3; x < this.cols + 3; x++) {
        if (!this.canPlace(type, rot, x, spawnY)) continue;
        const p: Active = { type, rot, x, y: spawnY, rowsFallen: 0, drifts: 0 };
        let guard = this.rows + 4;
        while (guard-- > 0 && this.fallOnce(p, list, false)) { /* fall */ }
        out.push({ x, rot, land: p });
      }
    }
    return out;
  }

  stats(): GameStats {
    return {
      score: this.score,
      lines: this.lines,
      level: this.level,
      pieces: this.pieces,
      trimmed: this.trimmed,
      maxCombo: this.maxCombo,
      quads: this.quads,
      ballastSent: this.ballastSent,
      ballastReceived: this.ballastReceived,
    };
  }
}
